/**
 * Shared vocabulary of the browser compute scheduler: lanes, job options, the context a running
 * job receives, the adapter interface a Worker client implements to join the scheduler, and the
 * error type every cancellation path rejects with.
 *
 * Why a separate module: adapters (inference, energy training, and the planning Workers)
 * import only these types, so registering a new Worker never pulls the scheduler's runtime into a
 * module that merely describes a protocol.
 *
 * @module apps/web/src/lib/compute/types
 */

/**
 * Priority lane of a job, from most to least urgent.
 *
 * - `interactive`: the person is waiting for this result right now (a preview they just asked
 *   for). Never paused by activity backoff, because the activity *is* the request.
 * - `visible`: the result changes what is on screen soon (progress of a retrain someone started,
 *   a refresh of visible data). Paused only while the page is hidden, because nobody can see it.
 * - `background`: speculative or periodic work (energy training, Worker pre-warming, prefetch).
 *   Starts only when the person is idle, and pauses whenever they interact.
 */
export type ComputeLane = 'interactive' | 'visible' | 'background';

/** Lanes in dispatch order; earlier lanes are always considered first. */
export const COMPUTE_LANES: readonly ComputeLane[] = ['interactive', 'visible', 'background'];

/**
 * What happens when a job is submitted with the key of a job that is still queued or running.
 *
 * - `supersede`: the newer request reflects the person's current intent, so the older job is
 *   aborted (its signal fires, its promise rejects with reason `superseded`).
 * - `join`: the work is idempotent (for example "retrain if stale"), so the caller receives the
 *   existing job's promise and no second job is created.
 */
export type ComputeDedupe = 'supersede' | 'join';

/** Why a job's signal fired and its promise rejected without a result. */
export type ComputeAbortReason = 'cancelled' | 'superseded' | 'deadline' | 'reset';

/**
 * Rejection used for every scheduler-initiated stop. Callers can branch on `reason` instead of
 * parsing messages; the message always contains the reason word so legacy `toThrow('cancelled')`
 * style checks keep working.
 */
export class ComputeAbortError extends Error {
  /** Constant name, mirroring DOM `AbortError` conventions for `instanceof`-free checks. */
  override readonly name = 'ComputeAbortError';

  /** Build an error for job `jobName` stopped for `reason`; `message` overrides the default text. */
  constructor(
    /** Why the job stopped. */
    readonly reason: ComputeAbortReason,
    /** Job label used in the default message. */
    jobName: string,
    /** Optional caller-facing message; defaults to "<job> <reason>." wording. */
    message?: string
  ) {
    super(
      message ??
        (reason === 'deadline'
          ? `${jobName} missed its deadline.`
          : reason === 'superseded'
            ? `${jobName} was superseded by a newer request.`
            : reason === 'reset'
              ? `${jobName} was reset.`
              : `${jobName} cancelled.`)
    );
  }
}

/** Why background work is currently held back, or null when it may run. */
export type ComputeBackoffReason =
  | 'input'
  | 'hidden'
  | 'save-data'
  | 'battery'
  | 'foreground'
  | null;

/**
 * Handle a running job receives. Everything here is advisory except `signal`: a job that ignores
 * pause notifications still finishes, but a job that ignores its signal keeps a concurrency slot
 * busy after its caller has already been rejected.
 */
export interface ComputeJobContext {
  /** Fires on cancellation, supersession, deadline or reset; `signal.reason` is a ComputeAbortError. */
  readonly signal: AbortSignal;
  /** Lane the job was dispatched on. */
  readonly lane: ComputeLane;
  /** Telemetry label of the job. */
  readonly name: string;
  /**
   * Current reason this job should pause, or null to continue. Only background jobs are ever told
   * to pause for input/foreground work; visible jobs are told to pause only while hidden.
   */
  backoff(): ComputeBackoffReason;
  /** Subscribe to pause/resume transitions; returns an unsubscribe function. */
  onBackoffChange(listener: (reason: ComputeBackoffReason) => void): () => void;
  /**
   * Yield the main thread at this lane's priority (via `scheduler.yield`/`postTask` when present).
   * Rejects with the abort reason if the job was stopped meanwhile, so chunk loops end promptly.
   */
  yield(): Promise<void>;
}

/** Options for one scheduled job. */
export interface ComputeJobOptions<T> {
  /** Stable telemetry label such as `inference` or `energy-training`; never personal data. */
  name: string;
  /** Priority lane. */
  lane: ComputeLane;
  /** The work itself. It must honour `context.signal` and should honour backoff when long. */
  run: (context: ComputeJobContext) => Promise<T> | T;
  /** Deduplication key; jobs with equal keys are joined or superseded (see `dedupe`). */
  key?: string;
  /** Behaviour for an equal key; defaults to `supersede` for foreground lanes and `join` for background. */
  dedupe?: ComputeDedupe;
  /** Milliseconds from submission after which the job is aborted with reason `deadline`. */
  deadlineMs?: number;
  /** Resource group (usually an adapter name) whose own concurrency limit also applies. */
  group?: string;
  /** Caller cancellation; aborting it rejects the job with reason `cancelled`. */
  signal?: AbortSignal;
  /** `performance.now()` timestamp before which the job may not start (for start-up quiet periods). */
  notBefore?: number;
  /**
   * Estimated run time $\hat c_j$ in milliseconds. With a deadline, a queued job whose estimate
   * can no longer fit ($t + \hat c_j > d_j$ at dispatch time $t$) expires instead of starting,
   * because it would occupy a slot only to miss anyway. Omitted means $\hat c_j = 0$.
   */
  estimatedMs?: number;
}

/** Observable state of a submitted job. */
export type ComputeJobState = 'queued' | 'running' | 'settled';

/** Returned by `ComputeScheduler.schedule`; the promise settles exactly once. */
export interface ComputeJobHandle<T> {
  /** Scheduler-unique job id (diagnostics only). */
  readonly id: number;
  /** Result of `run`, or a rejection (ComputeAbortError for scheduler stops). */
  readonly promise: Promise<T>;
  /** Current lifecycle state. */
  readonly state: ComputeJobState;
  /** Abort the job with reason `cancelled` (or the given reason). Safe to call repeatedly. */
  cancel(reason?: ComputeAbortReason): void;
}

/**
 * Registration contract for a Worker client. Implement this (or wrap an existing client with
 * `adaptWorkerClient`) and pass it to `ComputeScheduler.register` to put the Worker under the
 * scheduler's lanes, deduplication, deadlines, concurrency limits and telemetry.
 */
export interface ComputeAdapter<Request, Response> {
  /** Unique, stable adapter name; used as telemetry label and resource group. */
  readonly name: string;
  /** Lane used when a submission does not name one. */
  readonly lane: ComputeLane;
  /**
   * How many requests the underlying Worker(s) accept at once. Defaults to 1, which matches every
   * current Worker in the app: each rejects overlapping requests to protect shared WASM/GPU state.
   */
  readonly maxConcurrency?: number;
  /** Default deadline for submissions, in milliseconds from submission. */
  readonly deadlineMs?: number;
  /** Default estimated run time in milliseconds, used for deadline feasibility. */
  readonly estimatedMs?: number;
  /** Perform one request. Must stop (or at least settle) when `signal` aborts. */
  execute(request: Request, signal: AbortSignal, context: ComputeJobContext): Promise<Response>;
  /** Optional warm-up (load WASM, compile pipelines). Runs as a background job at idle. */
  prewarm?(signal: AbortSignal): Promise<void>;
  /** Optional teardown when the adapter is unregistered or the session resets. */
  reset?(reason: string): void;
}

/** Per-submission overrides for a registered adapter. */
export interface ComputeSubmitOptions {
  /** Lane override, for example `background` for a speculative call of an interactive adapter. */
  lane?: ComputeLane;
  /** Deduplication key; omitted means every submission is a separate job. */
  key?: string;
  /** Deduplication behaviour for `key`. */
  dedupe?: ComputeDedupe;
  /** Deadline override in milliseconds. */
  deadlineMs?: number;
  /** Caller cancellation. */
  signal?: AbortSignal;
  /** Earliest `performance.now()` start time. */
  notBefore?: number;
  /** Estimated run time in milliseconds (see `ComputeJobOptions.estimatedMs`). */
  estimatedMs?: number;
}

/** Options of `RegisteredCompute.prewarm`. */
export interface ComputePrewarmOptions {
  /**
   * Lane of the warm-up: `background` (speculative, waits for idle), `visible` (the feature is on
   * screen, for example the calendar mounted) or `interactive` (a click is imminent, for example a
   * pointer resting on Solve).
   */
  lane?: ComputeLane;
}

/** The handle `register` returns: submit work, request a warm-up, or leave the scheduler. */
export interface RegisteredCompute<Request, Response> {
  /** Adapter name. */
  readonly name: string;
  /** Queue one request under the adapter's lane, group and deadline. */
  submit(request: Request, options?: ComputeSubmitOptions): Promise<Response>;
  /**
   * Queue the adapter's `prewarm` as one deduplicated job in the adapter's resource group, so a
   * request that arrives meanwhile waits for the warm Worker instead of building a cold one.
   * `lane` (default `background`) states how sure the demand is: a repeated call with a more
   * urgent lane joins the queued or running warm-up and promotes it. A no-op without `prewarm`.
   */
  prewarm(options?: ComputePrewarmOptions): void;
  /** Cancel this adapter's queued and running jobs (reason `reset`) and keep it registered. */
  cancelAll(): void;
  /** Cancel this adapter's queued/running jobs, call its `reset`, and stop accepting work. */
  unregister(reason?: string): void;
}
